/**
 * Wrapper del menu navigazione contestuale
 *
 * ============================================================
 * FILOSOFIA v5.9.62
 * ============================================================
 *
 * "Il menu descrive la fase operativa corrente, non l'entità sottostante."
 *
 * Questo componente:
 * - Determina la fase corrente (Ingresso/Lavorazione/Commerciale/Dashboard)
 * - Mostra SOLO le voci rilevanti per quella fase
 * - NON duplica la sidebar (niente "Nuovo inserimento", "Pending", admin)
 * - Inietta badge dinamici dove necessario
 *
 * ============================================================
 */

import React, { useMemo, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AdaptiveMenu from '../ui-kit-dialogicom/layouts/AdaptiveMenu';
import type { MenuItem } from '../ui-kit-dialogicom/layouts/AdaptiveMenu';
import {
  getContextualMenuItems,
  getActiveNavigationId,
  getCurrentPhase,
  type WorkflowPhase
} from '../../config/navigationConfig';
import { usePendingCounts } from '../../contexts/PendingCountsContext';
import { useAuth } from '../auth/AuthProvider';

interface NavigationMenuWithBadgesProps {
  className?: string;
}

/**
 * Aggiunge ricorsivamente i badge ai menu items in base agli ID
 */
function addBadgesToItems(items: MenuItem[], badges: Record<string, number | undefined>): MenuItem[] {
  return items.map(item => {
    const badge = badges[item.id];
    const newItem: MenuItem = {
      ...item,
      badge: badge !== undefined ? badge : item.badge
    };

    // Processa ricorsivamente i children
    if (item.children && item.children.length > 0) {
      newItem.children = addBadgesToItems(item.children, badges);

      // Se un parent ha figli con badge, mostra la somma come badge sul parent
      const childBadgeSum = newItem.children.reduce((sum, child) => {
        return sum + (child.badge || 0);
      }, 0);

      // Mostra badge sul parent solo se non ha già un badge proprio
      if (childBadgeSum > 0 && !badge) {
        newItem.badge = childBadgeSum;
      }
    }

    return newItem;
  });
}

const NavigationMenuWithBadges: React.FC<NavigationMenuWithBadgesProps> = ({ className }) => {
  const location = useLocation();
  const { apparati, clienti, magazzino } = usePendingCounts();
  const { user } = useAuth();

  // Determina la fase corrente dal path
  const [currentPhase, setCurrentPhase] = useState<WorkflowPhase>(() => getCurrentPhase());

  // Aggiorna la fase quando cambia la location
  useEffect(() => {
    setCurrentPhase(getCurrentPhase(location.pathname));
  }, [location.pathname]);

  // Mappa ID menu → conteggio badge (contestuale alla fase)
  const totalPending = apparati + clienti;

  const badgeMap = useMemo(() => {
    // Badge globali (sempre visibili indipendentemente dalla fase)
    const globalBadges: Record<string, number | undefined> = {
      'accettazione-pending': totalPending > 0 ? totalPending : undefined, // Menu "In Pending" globale
      'magazzino': magazzino > 0 ? magazzino : undefined, // Badge sul pulsante Magazzino
    };

    // Badge specifici per fase INGRESSO
    if (currentPhase === 'ingresso') {
      return {
        ...globalBadges,
        'in-attesa': apparati, // Badge sul filtro "In Attesa di Conferma"
        'filtro-stato': apparati > 0 ? apparati : undefined // Badge sul parent se ci sono pending
      };
    }

    // Per altre fasi, mostra comunque i badge globali
    return globalBadges;
  }, [currentPhase, apparati, clienti, magazzino, totalPending]);

  // I flag di visibilita' decisi dal superadmin arrivano dal server: senza questa
  // lettura il browser dell'admin non li conoscerebbe mai e le voci condizionate
  // resterebbero invisibili anche dopo l'abilitazione.
  const [flagsCaricati, setFlagsCaricati] = useState(0);
  useEffect(() => {
    let annullato = false;
    fetch('/api/admin/magazzino-sync/ui-flags', {
      headers: { 'x-user-role': user?.role || '', 'x-user-group': user?.group_id || '' },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (annullato || !d?.success) return;
        const v = d.magazzinoSyncVisibileAdmin ? '1' : '0';
        if (localStorage.getItem('magazzino_sync_visibile_admin') !== v) {
          localStorage.setItem('magazzino_sync_visibile_admin', v);
          setFlagsCaricati(n => n + 1); // forza il ricalcolo del menu
        }
      })
      .catch(() => { /* il menu resta com'e': il server nega comunque l'accesso */ });
    return () => { annullato = true; };
  }, [user?.role, user?.group_id]);

  // Ottieni menu items per la fase corrente (filtrati per ruolo/gruppo utente)
  const contextualItems = useMemo(() => {
    return getContextualMenuItems(currentPhase, user?.role, user?.group_id);
  }, [currentPhase, user?.role, user?.group_id, flagsCaricati]);

  // Inietta i badge nei menu items
  const itemsWithBadges = useMemo(() => {
    return addBadgesToItems(contextualItems, badgeMap);
  }, [contextualItems, badgeMap]);

  // Se non ci sono items (fase "other" = admin, profilo, test), non mostrare il menu
  if (itemsWithBadges.length === 0) {
    return null;
  }

  return (
    <AdaptiveMenu
      items={itemsWithBadges}
      activeItemId={getActiveNavigationId()}
      className={className}
    />
  );
};

export default NavigationMenuWithBadges;
